import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable} from 'rxjs';
import {SketchLineObject, SketchPointObject,} from '../mesh/sketch';
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {PointSnapStrategyType} from '../manager/pointSnap/PointSnapStrategyType';
import type {PointSnapResult} from '../manager/pointSnap/PointSnapTypes';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {isDashDotLIneNodeMaterial, LineMaterialFactory, SketchEdgeBaseStyle} from '../material';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';
import {
    SketchRelationKind,
    type SketchRelationKind as SketchRelationKindValue,
} from '../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';

/**
 * 线绘制命令枚举。
 */
export enum SketchLineCommand {
    /** 直线命令。 */
    Line = 'sketch-line',
    /** 中心线命令。 */
    CenterLine = 'sketch-line-center',
    /** 中点线命令。 */
    MidpointLine = 'sketch-line-midpoint',
}

/**
 * 判断命令是否为线绘制命令。
 * @param command 命令字符串。
 * @returns 是否为线绘制命令。
 */
export function isSketchLineCommand(command: string): command is SketchLineCommand {
    return Object.values(SketchLineCommand).includes(command as SketchLineCommand);
}

/**
 * 线方向枚举。
 */
export enum LineDirection {
    /** 按绘制方向。 */
    AsSketched = 'as-sketched',
    /** 水平方向。 */
    Horizontal = 'horizontal',
    /** 垂直方向。 */
    Vertical = 'vertical',
    /** 角度方向。 */
    Angle = 'angle',
}

/**
 * 线绘制选项。
 */
export interface LineOptions {
    /** 线绘制类型。 */
    type: SketchLineCommand;
    /** 线方向。 */
    direction: LineDirection;
    /** 角度。 */
    angle: number;
    /** 是否为构造几何。 */
    forConstruction: boolean;
    /** 是否无限长度。 */
    infiniteLength: boolean;
}

/**
 * 线绘制参数。
 */
export interface LineParameters {
    /** 长度。 */
    length?: number;
    /** 角度。 */
    angle?: number;
}

/**
 * 草图线绘制处理器。
 */
export class SketchLineDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Line;
    /** 线工具选项。 */
    public lineOptions: LineOptions = {
        type: SketchLineCommand.Line,
        direction: LineDirection.AsSketched,
        angle: 0,
        forConstruction: false,
        infiniteLength: false,
    };
    /** 预览线对象（由本处理器持有）。 */
    private previewLineObject: SketchLineObject | null = null;
    /** 预览线起点。 */
    private previewStartPoint: SketchPointObject | null = null;
    /** 预览线终点。 */
    private previewEndPoint: SketchPointObject | null = null;
    /** 中点线是否已在当前链路中生效。 */
    public midpointLineUsed: boolean = false;
    /** 当前连续画线链路 ID。 */
    public lineChainId: string | null = null;
    /** 当前连续画线链路起点。 */
    public lineChainStartPoint: THREE.Vector3 | null = null;
    /** 当前连续画线链路已提交段数。 */
    public lineChainSegmentCount: number = 0;
    /** 最近创建的线段对象。 */
    public lastCreatedLine: SketchLineObject | null = null;
    /** 最近创建线段时间戳。 */
    public lastLineCreatedAt: number = 0;

    /** 当前绘制点对应的捕捉来源点对象。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];
    /** 当前绘制点对应的单击接受捕捉结果。 */
    private drawingPointSnapResults: Array<PointSnapResult | null> = [];


    /** 当前连续画线链路中已创建的线段集合。 */
    private currentLineChainLines: SketchLineObject[] = [];

    /** 当前线绘制点数量流。 */
    private readonly drawingPointCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    /** 预览线对象变更流。 */
    private readonly lineParametersSubject: BehaviorSubject<SketchLineObject | null> = new BehaviorSubject<SketchLineObject | null>(null);

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    
    getRequiredPointCount(): number {
        return 2;
    }

    
    get closable(): boolean {
        return true;
    }

    
    getDrawingHint(): string {
        return '点击起点，连续点击绘制线段（双击结束） | ESC取消';
    }

    
    resetState(): void {
        this.drawingPoints = [];
        this.disposePreviewLine();
        this.lineParametersSubject.next(null);
        this.midpointLineUsed = false;
        this.lastCreatedLine = null;
        this.lastLineCreatedAt = 0;

        this.drawingPointTargets = [];
        this.drawingPointSnapResults = [];

        this.lineChainId = null;
        this.lineChainStartPoint = null;
        this.lineChainSegmentCount = 0;

        this.currentLineChainLines = [];

        this.syncDrawingPointCountChange();
    }

    
    onActivate(_tool: SketchDrawingTool): void {
        // 线工具激活时初始化链路 ID。
        this.lineChainId = `line-chain-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    }

    /**
     * @description 监听线绘制点数量变化。
     * @returns 点数量只读流。
     */
    public observeDrawingPointCount(): Observable<number> {
        return this.drawingPointCountSubject.asObservable();
    }

    /**
     * @description 监听预览线对象变更（Vue 从线对象的 getLength/angleDeg 获取参数）。
     * @returns 预览线对象只读流。
     */
    public observeLineParameters(): Observable<SketchLineObject | null> {
        return this.lineParametersSubject.asObservable();
    }

    /**
     * @description 获取当前预览线对象。
     * @returns 当前预览线对象；不存在时返回 null。
     */
    public getPreviewLineObject(): SketchLineObject | null {
        return this.previewLineObject;
    }

    /**
     * @description 同步推送当前线绘制点数量。
     */
    public syncDrawingPointCountChange(): void {
        this.drawingPointCountSubject.next(this.drawingPoints.length);
    }

    /**
     * 撤销直线命令内尚未提交的输入点。
     *
     * 说明：
     * 1. 连续直线在提交一段后会保留上一段终点作为下一段锚点；
     * 2. 这个锚点已经对应了模型中的已提交线段，不能被命令内 Ctrl+Z 消费；
     * 3. 返回 false 后上层会继续走全局模型 undo，从而撤销上一条已提交线段。
     *
     * @returns 是否撤销了尚未提交的直线输入点。
     */
    public override undoLastDrawingPoint(): boolean {
        if (this.drawingPoints.length === 0) {
            return false;
        }
        if (this.drawingPoints.length === 1 && this.lineChainSegmentCount > 0) {
            return false;
        }

        this.drawingPoints.pop();
        this.drawingPointTargets = this.drawingPointTargets.slice(0, this.drawingPoints.length);
        this.drawingPointSnapResults = this.drawingPointSnapResults.slice(0, this.drawingPoints.length);
        this.syncDrawingPointCountChange();
        this.disposePreviewLine();
        this.destroyPreview();
        this.lineParametersSubject.next(null);

        return true;
    }

    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void
    updatePreview(currentPoint: THREE.Vector3, _currentVersion?: number, _tool?: unknown, startPointOverride?: THREE.Vector3): void {
        // 长度属性可在连续绘制中改变上一段终点，预览前同步权威锚点。
        this.syncActiveLineChainAnchor();
        // 线预览保持"首点 -> 当前点"的历史行为；快速相切捕捉时用 startPointOverride 覆盖首点。
        const firstPoint = startPointOverride ?? this.drawingPoints[0];
        if (!firstPoint) {
            return;
        }

        // 预览阶段复用统一端点解析，确保方向约束与创建阶段一致。
        const resolved = this.resolveLinePoints(firstPoint, currentPoint);
        if (!resolved) {
            return;
        }
        // 创建或更新预览线对象，派发更新事件供 Vue 读取。
        this.createLinePreviewInHandler(resolved.start, resolved.end);
    }

    /**
     * 处理线绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        if (this.shouldFinishByNativeDoubleClick(payload.event)) {
            return;
        }
        // 本次落点必须从属性求解后的新终点继续，避免使用旧坐标断开线链。
        this.syncActiveLineChainAnchor();
        // 优先使用上游传入的吸附点（切线/垂线投影点或普通捕捉点），保证精确性。
        const point = payload.snappedPoint ?? this.getDrawingPoint(payload.event);
        if (!this.lineChainStartPoint && this.drawingPoints.length === 0) {
            this.lineChainStartPoint = point.clone();
            this.lineChainSegmentCount = 0;
        }

        if (this.shouldFinishByClickInterval()) {
            this.finishLineChainDrawing();
            return;
        }

        this.drawingPoints.push(point);

        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);
        // 捕捉语义与落点按相同索引保存，确保第二点提交只消费单击瞬间接受的结果。
        this.drawingPointSnapResults.push(payload.snapResult ?? null);

        this.syncDrawingPointCountChange();
        this.processLineDrawingStepInHandler();
    }

    /**
     * 解析线端点（含方向约束、中点线、无限延伸、坐标系转换等）。
     * @param startPoint 世界坐标起点。
     * @param endPoint 世界坐标终点。
     * @returns 解析后的线端点信息，或 null 表示无法解析。
     * @throws 当当前绘制引用平面缺失时抛出错误。
     */
    public resolveLinePoints(startPoint: THREE.Vector3, endPoint: THREE.Vector3): {
        start: THREE.Vector3;
        end: THREE.Vector3;
        anchorEnd: THREE.Vector3;
        startLocal: THREE.Vector2;
        endLocal: THREE.Vector2;
    } | null {
        if (!this.referencePlane) {
            throw new Error();
        }
        const startLocalRaw = this.referencePlane.worldToLocal(startPoint);
        const endLocalRaw = this.referencePlane.worldToLocal(endPoint);
        const startLocal = new THREE.Vector2(startLocalRaw.x, startLocalRaw.y);
        const endLocal = new THREE.Vector2(endLocalRaw.x, endLocalRaw.y);

        // 方向约束：水平/垂直/角度
        if (this.lineOptions.direction === LineDirection.Horizontal) {
            endLocal.y = startLocal.y;
        } else if (this.lineOptions.direction === LineDirection.Vertical) {
            endLocal.x = startLocal.x;
        } else if (this.lineOptions.direction === LineDirection.Angle) {
            const dx = endLocal.x - startLocal.x;
            const dy = endLocal.y - startLocal.y;
            const length = Math.hypot(dx, dy) || 0;
            const angleRad = THREE.MathUtils.degToRad(this.lineOptions.angle);
            endLocal.x = startLocal.x + Math.cos(angleRad) * length;
            endLocal.y = startLocal.y + Math.sin(angleRad) * length;
        }

        const anchorEnd = this.referencePlane.localToWorld(new THREE.Vector2(endLocal.x, endLocal.y));

        let lineStartLocal = startLocal.clone();
        let lineEndLocal = endLocal.clone();
        // 中点线处理
        const useMidpoint = this.lineOptions.type === SketchLineCommand.MidpointLine && !this.midpointLineUsed;

        if (useMidpoint) {
            lineStartLocal = startLocal.clone().multiplyScalar(2).sub(endLocal);
            lineEndLocal = endLocal.clone();
        }

        // 无限延伸处理
        if (this.lineOptions.infiniteLength) {
            const direction = endLocal.clone().sub(startLocal);
            if (direction.length() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLength) {
                direction.set(1, 0);
            }
            direction.normalize();
            const extendLength = 1000;
            lineStartLocal = startLocal.clone().addScaledVector(direction, -extendLength);
            lineEndLocal = startLocal.clone().addScaledVector(direction, extendLength);
        }

        const start = this.referencePlane.localToWorld(new THREE.Vector2(lineStartLocal.x, lineStartLocal.y));
        const end = this.referencePlane.localToWorld(new THREE.Vector2(lineEndLocal.x, lineEndLocal.y));

        return {
            start,
            end,
            anchorEnd,
            startLocal,
            endLocal,
        };
    }


    /**
     * 创建草图直线实体。
     * @returns 新线段的锚定终点（用于连续绘制的起点），或 null 表示创建失败。
     * @throws 当当前绘制引用平面缺失时抛出错误。
     */
    public createLine(): THREE.Vector3 | null {
        if (this.drawingPoints.length < 2 ) return null;
        if (!this.referencePlane) {
            throw new Error();
        }
        const startPoint = this.drawingPoints[0];
        const endPoint = this.drawingPoints[1];
        // 解析线端点（含中点线、坐标系转换等）。
        const resolved = this.resolveLinePoints(startPoint, endPoint);
        if (!resolved) {
            return null;
        }

        const isCenterline = this.lineOptions.type === SketchLineCommand.CenterLine;
        const isConstruction = isCenterline || this.lineOptions.forConstruction;
        const baseStyle = isConstruction
            ? SketchEdgeBaseStyle.Construction
            : SketchEdgeBaseStyle.Basic;

        const material = this.createSketchLineMaterial(isConstruction, isCenterline);
        const snappedStartControlPoint = this.resolveSnappedLineControlPoint(0, resolved.start);
        const reusedStartControlPoint = snappedStartControlPoint
            ?? this.resolveReusableLineStartControlPoint(resolved.start);
        const startSourcePoint = reusedStartControlPoint
            ? null
            : this.resolveLineControlPointSource(0, resolved.start);
        const startControlPoint = reusedStartControlPoint
            ?? this.createLineControlPoint(resolved.start, false, startSourcePoint);
        const snappedEndControlPoint = this.resolveSnappedLineControlPoint(1, resolved.end);
        const endControlPoint = snappedEndControlPoint
            ?? this.createLineControlPoint(
                resolved.end,
                false,
                this.resolveLineControlPointSource(1, resolved.end),
            );


        // 创建 SketchLineObject 实体。
        const line = new SketchLineObject({
            startPoint: startControlPoint,
            endPoint: endControlPoint,
            material,
            referencePlane: this.referencePlane,
            isConstruction,
            baseStyle,
            isInfiniteLength: this.lineOptions.infiniteLength,

            isCenterline,

        });

        if (material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(material)) {
            line.computeLineDistances();
        }
        // 如果在链路中，绑定前一条线的终点与当前线的起点。
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(startControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(endControlPoint);

        // 添加到草图实体集合。
        this.viewer.addSketchObject(line);
        if (!reusedStartControlPoint) {
            this.viewer.addSketchObject(startControlPoint);
        }
        if (!snappedEndControlPoint) {
            this.viewer.addSketchObject(endControlPoint);
        }

        // 更新链路状态。
        this.lastCreatedLine = line;
        this.lastLineCreatedAt = Date.now();
        this.lineChainSegmentCount += 1;

        this.currentLineChainLines.push(line);

        if (this.lineOptions.type === SketchLineCommand.MidpointLine && !this.midpointLineUsed) {
            this.midpointLineUsed = true;
        }
        return resolved.anchorEnd.clone();
    }

    /**
     *
     * 根据当前线方向选项或已接受捕捉结果创建线段方向关系。
     * 说明：
     * 1. 属性面板固定水平/竖直方向时沿用对应 self 关系；
     * 2. 按绘制方向时只消费单击瞬间接受的 H/V 捕捉语义，不根据最终坐标猜测；
     * 3. 关系由 Viewer 唯一服务创建，并由外层绘制事务统一记录历史。
     * @param line 新创建的线段对象。
     * @param acceptedSnapResult 单击终点时接受的捕捉结果。
     */
    public applyLineDirectionRelation(
        line: SketchLineObject,
        acceptedSnapResult: PointSnapResult | null = null,
    ): void {
        if (!this.isAutoGeometryRelationsEnabled()) {
            return;
        }
        const relationKind = this.resolveLineDirectionRelationKind(acceptedSnapResult);
        if (!relationKind) {
            return;
        }
        this.viewer.sketchRelationCreationService.createRelation({
            relationKind,
            objects: [line],
            // 线与自动关系属于同一次用户点击，由 sketch.runtime.draw.line 父事务统一提交。
            historyMode: 'parent-managed',
        });
    }

    /**
     * 解析当前直线提交应创建的方向关系类型。
     *
     * @param acceptedSnapResult 单击终点时接受的捕捉结果。
     * @returns 水平或竖直关系类型；本次提交不应创建方向关系时返回 null。
     */
    private resolveLineDirectionRelationKind(
        acceptedSnapResult: PointSnapResult | null,
    ): SketchRelationKindValue | null {
        if (this.lineOptions.direction === LineDirection.Horizontal) {
            return SketchRelationKind.Horizontal;
        }
        if (this.lineOptions.direction === LineDirection.Vertical) {
            return SketchRelationKind.Vertical;
        }
        if (
            this.lineOptions.direction !== LineDirection.AsSketched
            || acceptedSnapResult?.strategyType !== PointSnapStrategyType.HorizontalVertical
        ) {
            return null;
        }
        if (
            acceptedSnapResult.suggestedRelationKind === SketchRelationKind.Horizontal
            || acceptedSnapResult.suggestedRelationKind === SketchRelationKind.Vertical
        ) {
            return acceptedSnapResult.suggestedRelationKind;
        }
        return null;
    }


    /**
     * 完成线链绘制，清理绘制状态。
     */
    public finishLineChainDrawing(): void {
        if (!this.viewer.sketchDrawingManager || (this.sketchDrawingManager.getCurrentTool() || null) !== SketchDrawingTool.Line) {
            return;
        }
        // 清理绘制点和预览。
        this.drawingPoints = []
        this.syncDrawingPointCountChange();
        this.sketchDrawingManager.setPreviewPoint(null);
        this.disposePreviewLine();
        this.destroyPreview();
        // 广播清除所有方向型捕捉策略的预览状态（引导线等场景对象）。
        this.sketchDrawingManager.viewer.pointSnapManager?.clearAllPreviews();
        // this.sketchDrawingManager.setLineSnappingState(false);
        // 清理线链状态。
        this.lineChainStartPoint = null;
        this.lineChainSegmentCount = 0;
        this.lastCreatedLine = null;
        this.lastLineCreatedAt = 0;
        this.midpointLineUsed = false;

        this.drawingPointTargets = [];
        this.drawingPointSnapResults = [];


        this.currentLineChainLines = [];

        this.lineChainId = `line-chain-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

        // this.sketchDrawingManager.clearLineFirstPointPreview();

    }

    /**
     * 在处理器内创建或更新预览线对象。
     * 说明：
     * 1. 首次调用创建 SketchLineObject 并添加到场景；
     * 2. 后续调用仅更新端点位置，几何由端点联动自动刷新；
     * 3. 材质从 LineMaterialFactory 获取单例实例。
     * @param startPoint 起点。
     * @param endPoint 终点。
     */
    private createLinePreviewInHandler(startPoint: THREE.Vector3, endPoint: THREE.Vector3): void {
        // 判断是否为构造线预览。
        const isConstructionPreview = this.lineOptions.forConstruction
            || this.lineOptions.type === SketchLineCommand.CenterLine;
        // 从工厂获取单例材质。
        const material = isConstructionPreview
            ? LineMaterialFactory.getPreviewDashedMaterial()
            : LineMaterialFactory.getPreviewBasicMaterial();

        if (!this.previewLineObject) {
            // 首次创建预览线对象。
            this.previewStartPoint = this.createLineControlPoint(startPoint);
            this.previewEndPoint = this.createLineControlPoint(endPoint);
            this.previewLineObject = new SketchLineObject({
                startPoint: this.previewStartPoint,
                endPoint: this.previewEndPoint,
                material,
                referencePlane: this.referencePlane,

                // 将该线标记为绘制态预览线，供点捕捉链路统一忽略。
                isDrawingPreviewLine: true,

                isConstruction: isConstructionPreview,
                isInfiniteLength: this.lineOptions.infiniteLength,
            });
            // 覆写构造函数中被工厂替换的材质为预览材质。
            this.previewLineObject.material = material;
            if (material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(material)) {
                this.previewLineObject.computeLineDistances();
            }
        this.viewer.addObject(this.previewLineObject);
        } else {
            // 更新预览线端点位置（setPosition 会联动刷新线几何）。
            this.previewStartPoint!.setPosition(startPoint);
            this.previewEndPoint!.setPosition(endPoint);
            // 构造线状态可能在绘制中被切换，需要更新材质。
            if (this.previewLineObject.material !== material) {
                this.previewLineObject.material = material;
            }
            if (material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(material)) {
                this.previewLineObject.computeLineDistances();
            }
        }
        // 派发预览线对象更新事件。
        this.lineParametersSubject.next(this.previewLineObject);
    }

    /**
     * 释放预览线对象及其端点，从场景中移除。
     * 说明：仅释放几何体，材质由工厂统一管理不做 dispose。
     */
    private disposePreviewLine(): void {
        if (this.previewLineObject && this.viewer.scene) {
            this.viewer.scene.remove(this.previewLineObject);
            this.previewLineObject.geometry.dispose();
        }
        this.previewLineObject = null;
        this.previewStartPoint = null;
        this.previewEndPoint = null;
    }

    /**
     * 在处理器内执行线绘制单步提交。
     * 说明：
     * 1. 仅在 line 工具下生效；
     * 2. 提交后保留锚点作为下一段起点，保持历史连续绘制行为。
     */
    private processLineDrawingStepInHandler(): void {
        if (this.drawingPoints.length !== 2) {
            return;
        }
        // 在闭合收口可能清空交互状态前保留本段终点接受的捕捉语义。
        const acceptedSnapResult = this.drawingPointSnapResults[1] ?? null;
        const anchorPoint = this.createLine();
        // 创建失败时 lastCreatedLine 可能仍指向上一段，禁止把本次捕捉关系错误挂到历史线段。
        const createdLine = anchorPoint ? this.lastCreatedLine : null;
        const didFinalizeClosedChain = this.tryFinalizeClosedLineChainCollection();

        if (createdLine) {
            // 闭合集合收口完成后再提交关系定义，确保参数定义快照包含本次完整绘制终态。
            this.applyLineDirectionRelation(createdLine, acceptedSnapResult);
            // 选择中心是线属性面板唯一权威来源，完成线段后立即同步当前线。
            this.viewer.entitySelectionManager?.selectEntityProgrammatically(createdLine);
        }

        if (didFinalizeClosedChain) {
            return;
        }

        this.drawingPoints = []

        this.drawingPointTargets = [];
        this.drawingPointSnapResults = [];

        if (anchorPoint) {
            this.drawingPoints.push(anchorPoint);

            this.drawingPointTargets.push(this.lastCreatedLine?.endPoint ?? null);
            // 连续线下一段的锚点来自已提交实体，不继承上一段终点的捕捉关系建议。
            this.drawingPointSnapResults.push(null);

        }
        this.syncDrawingPointCountChange();
        // 清除绘制预览。
        this.disposePreviewLine();
        this.destroyPreview();
    }

    /**
     * 从最近创建线段的终点同步连续绘制锚点。
     *
     * 设计说明：
     * 1. 完成一段后，`drawingPoints[0]` 是下一段的临时起点；
     * 2. Enter 提交长度会通过参数系统移动真实终点，临时起点必须跟随；
     * 3. `getPositionTo` 直接复用现有向量，不在鼠标移动热路径中频繁创建对象。
     */
    private syncActiveLineChainAnchor(): void {
        if (
            this.drawingPoints.length !== 1
            || this.lineChainSegmentCount <= 0
            || !this.lastCreatedLine
        ) {
            return;
        }
        this.lastCreatedLine.endPoint.getPositionTo(this.drawingPoints[0]);
        this.drawingPointTargets[0] = this.lastCreatedLine.endPoint;
    }

    /**
     * 判断是否应由浏览器原生双击事件直接结束线链绘制。
     * @param event 鼠标按下事件。
     * @returns 是否已完成结束处理。
     */
    private shouldFinishByNativeDoubleClick(
        event: MouseEvent,
    ): boolean {
        if (event.detail < 2 || this.drawingPoints.length < 1) {
            return false;
        }
        this.finishLineChainDrawing();
        return true;
    }

    /**
     * 判断是否应由“短时间连续点击”结束线链绘制。
     * @returns 是否满足结束条件。
     */
    private shouldFinishByClickInterval(): boolean {
        const currentTime = Date.now()
        const timeSinceLastClick = currentTime - this.sketchDrawingManager.getLastLineClickTimestamp();
        if (
            timeSinceLastClick < this.sketchDrawingManager.getLineDoubleClickThreshold()
            && this.drawingPoints.length >= 1
        ) {
            return true;
        }
        this.sketchDrawingManager.setLastLineClickTimestamp(currentTime);
        return false;
    }


    /**
     * 解析当前连续画线链路中的上一条线。
     * @returns 上一条线对象；不存在时返回 `null`。
     */
    private resolvePreviousLineInActiveChain(): SketchLineObject | null {
        if (!this.lineChainId || this.lineChainSegmentCount <= 0 || !this.lastCreatedLine) {
            return null;
        }
        return this.lastCreatedLine;
    }

    /**
     * 解析是否可复用上一段的终点作为本段起点。
     * 规则：
     * 1. 仅连续画线链路允许复用；
     * 2. 点位需与本段起点在容差内；
     * 3. 复用后可保证"前段终点 = 后段起点"为同一控制点对象。
     * @param expectedStartPoint 本段预期起点。
     * @returns 可复用控制点对象；否则返回 `null`。
     */
    private resolveReusableLineStartControlPoint(expectedStartPoint: THREE.Vector3): SketchPointObject | null {
        const previousLine = this.resolvePreviousLineInActiveChain();
        if (!previousLine) {
            return null;
        }
        const previousEndPoint = previousLine.endPoint;
        const previousEndPosition = previousEndPoint.getPosition();
        const distanceSquared = previousEndPosition.distanceToSquared(expectedStartPoint);
        if (
            distanceSquared
            > SKETCH_GEOMETRY_TOLERANCE_CONFIG.drawingClosureSnap
            * SKETCH_GEOMETRY_TOLERANCE_CONFIG.drawingClosureSnap
        ) {
            return null;
        }
        return previousEndPoint;
    }


    /**
     * 解析当前绘制点是否命中了可记录为来源的草图控制点。
     * 说明：
     * 1. 捕捉目标点对象与解析后的实际点位仍在容差内时才记录来源；
     * 2. 若线方向约束修改了终点位置，导致点位偏离原捕捉点，则放弃记录；
     * 3. 该方法只返回来源引用，不再把外部捕捉点直接复用为线端点。
     * @param pointIndex 当前绘制点索引。
     * @param expectedPoint 解析后的目标点位。
     * @returns 可记录的来源点对象；否则返回 null。
     */
    private resolveLineControlPointSource(
        pointIndex: number,
        expectedPoint: THREE.Vector3,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex];
        return this.resolveControlPointSource(targetPoint, expectedPoint);
    }

    /**
     * 解析当前捕捉点是否可直接复用为直线端点控制点。
     * 说明：
     * 1. 仅当捕捉来源点与直线解析后的端点位置仍在闭合容差内时才允许复用；
     * 2. 中点、象限点属于宿主几何派生的辅助捕捉点，只能作为来源引用，不能被提升为新线段端点；
     * 3. 非派生正式点不依赖自动几何关系开关，避免端点捕捉被误伤。
     * @param pointIndex 当前绘制点索引。
     * @param expectedPoint 解析后的端点位置。
     * @returns 可复用的草图点对象；否则返回 null。
     */
    private resolveSnappedLineControlPoint(
        pointIndex: number,
        expectedPoint: THREE.Vector3,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex];
        const reusablePoint = this.resolveControlPointSource(targetPoint, expectedPoint);
        if (!reusablePoint) {
            return null;
        }

        // 派生捕捉点的显示与宿主几何绑定，复用成端点会污染其拓扑角色并破坏下次捕捉显示。
        if (this.isDerivedSnapControlPoint(reusablePoint)) {
            return null;
        }

        return reusablePoint;
    }


    /**
     * 判断捕捉点是否为宿主几何派生的辅助捕捉点。
     * 说明：
     * 1. 中点由线段、圆弧等宿主几何派生，默认应随宿主几何按需显示；
     * 2. 象限点由圆、圆弧、椭圆等宿主几何派生，默认同样应保持辅助点语义；
     * 3. 直线绘制命中这些点时只记录来源引用，避免新端点改变原辅助点的拓扑角色。
     * @param point 捕捉命中的候选点。
     * @returns 若该点是派生辅助捕捉点则返回 true。
     */
    private isDerivedSnapControlPoint(point: SketchPointObject): boolean {
        return point.isMidPoint || point.isQuadrantPoint;
    }


    /**
     * 尝试将当前线链收口为闭合线段集合。
     * 说明：
     * 1. 仅在线段已经自然闭合时才会创建集合，不改写当前点击点；
     * 2. 收口成功后只结束当前线链，不退出直线绘制工具；
     * 3. 集合对象统一复用当前链路已创建的正式线段与控制点。
     * @returns 收口成功返回 true，否则返回 false。
     */
    private tryFinalizeClosedLineChainCollection(): boolean {
        const activeSketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup;
        if (!activeSketchGroup) {
            return false;
        }

        const collectionObject = activeSketchGroup.tryFinalizeClosedSketchCollection(
            this.currentLineChainLines,
            {syncContour: this.viewer.shadedSketchContoursEnabled},
        );
        if (!collectionObject) {
            return false;
        }

        this.sketchDrawingManager.notifySketchEntityCreated(collectionObject);
        this.viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);
        this.finishLineChainDrawing();
        return true;
    }

}



